// Quick-add a crew person to the producer's reusable team ("My Team")
// straight from the crew step. Renders a plus icon (hover: "Add to My
// Team"); once the person's name OR email already exists in the team it
// shows a disabled check instead. Kept as its own component so the crew
// step's group header stays readable and the add/duplicate behaviour is
// unit-testable in isolation.

import { UserCheck, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { findTeamMemberDuplicate, type FormState, type TeamMember } from "@/lib/teamMembers";

interface AddToTeamButtonProps {
  /** The crew person to save (function = their crew role). */
  member: { name: string; email: string; phone: string | null; function: string };
  /** Current My Team members, for the name/email duplicate check. */
  existing: Array<Pick<TeamMember, "name" | "email">>;
  /** True while an add is in flight (shared mutation across rows). */
  isPending: boolean;
  /** Called with the team payload when the producer adds this person. */
  onAdd: (form: FormState) => void;
}

export const AddToTeamButton = ({ member, existing, isPending, onAdd }: AddToTeamButtonProps) => {
  const canAdd = !!member.name.trim() && !!member.email.trim();
  const alreadyInTeam =
    canAdd &&
    findTeamMemberDuplicate(existing, { name: member.name, email: member.email }) !== null;

  if (alreadyInTeam) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button variant="ghost" size="icon" disabled aria-label="Already in My Team">
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Already in My Team</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={!canAdd || isPending}
          onClick={(e) => {
            e.stopPropagation();
            onAdd({
              name: member.name,
              email: member.email,
              phone: member.phone || "",
              role: member.function || "",
              gear: [],
            });
          }}
          aria-label="Add to My Team"
        >
          <UserPlus className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{canAdd ? "Add to My Team" : "Add a name and email first"}</TooltipContent>
    </Tooltip>
  );
};
